import { useEffect, useRef, useState } from "react";
import { Formik } from "formik";
import { useNavigate } from "react-router-dom";
import { Slide, ToastContainer, toast } from "react-toastify";
import { DefaultLayout } from "../../layouts/Default";
import { Main } from "../../themes/Default/components/Main";
import { Tabs } from "../../themes/Default/components/Tabs";
import { TopNavigation } from "../../themes/Default/components/TopNavigation";
import { Breadcrumps } from "../../themes/Default/components/Breadcrumps";
import { Link } from "../../themes/Default/components/Breadcrumps/components/Link";
import { Button, ButtonBack } from "../../themes/Default/components/TopNavigation/components/Button";
import { Form } from "../../themes/Default/components/Form";
import { Row } from "../../themes/Default/components/Form/components/Row";
import { Divider } from "../../themes/Default/components/Divider";
import { Field } from "../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../themes/Default/components/Form/components/Input";
import { Select } from "../../themes/Default/components/Form/components/Select";
import { employeesManagement } from "../../hooks/api/EmployeesManagement";
import { prepareSelect } from "../../services/core/Select";
import { useTranslation } from "react-i18next";
import { employeeRolesManagement } from "../../hooks/api/EmployeeRolesManagement/EmployeeRolesManagement";

const AddEmployee = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const [formErrors, setFormErrors] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const { useAddEmployeeMutation } = employeesManagement;
    const [addEmployee, { isLoading: isAddingEmployee }] = useAddEmployeeMutation();
    const { useRoleListMutation } = employeeRolesManagement;
    const [roleList] = useRoleListMutation();

    const tabs = [{ title: t("details"), link: "/settings/reports", isActive: true }];

    const onSave = () => {
        if (formRef.current) {
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        addEmployee(values).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                navigate("/employees");
            }
        });
    };

    useEffect(() => {
        roleList().then((result) => {
            let options = [];
            result?.data?.records.forEach((role) => {
                options.push({
                    id: role.id,
                    name: role.name,
                });
            });
            setRoleOptions(prepareSelect(options));
        });
    }, [roleList]);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/employees" />
                <Button title={t("save")} isDisabled={isAddingEmployee} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("employees.title")} icon="fa-user-tie" />
                    <Link title={t("details")} />
                    <Link title={t("employees.addEmployee")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        role: "",
                        firstName: "",
                        lastName: "",
                        phoneNumber: "",
                        phoneNumberPrivate: "",
                        emailAddress: "",
                        emailAddressPrivate: "",
                        googleMeetingLink: "",
                        addressStreet: "",
                        addressNumber: "",
                        addressCity: "",
                        addressZipCode: "",
                        socialMediaFacebook: "",
                        socialMediaInstagram: "",
                        socialMediaYoutube: "",
                        socialMediaLinkedIn: "",
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Select
                                        name="role"
                                        label={t("employees.role")}
                                        options={roleOptions}
                                        onChange={handleChange}
                                        selectedValue={values.role}
                                        error={formErrors?.role}
                                        required={true}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="firstName"
                                        label={t("employees.firstName")}
                                        required={true}
                                        value={values.firstName}
                                        onChange={handleChange}
                                        error={formErrors?.firstName}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="lastName"
                                        label={t("employees.lastName")}
                                        required={true}
                                        value={values.lastName}
                                        onChange={handleChange}
                                        error={formErrors?.lastName}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="phoneNumber"
                                        label={t("employees.phoneNumber")}
                                        value={values.phoneNumber}
                                        onChange={handleChange}
                                        error={formErrors?.phoneNumber}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="phoneNumberPrivate"
                                        label={t("employees.phoneNumberPrivate")}
                                        value={values.phoneNumberPrivate}
                                        onChange={handleChange}
                                        error={formErrors?.phoneNumberPrivate}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="emailAddress"
                                        label={t("employees.email")}
                                        required={true}
                                        value={values.emailAddress}
                                        onChange={handleChange}
                                        error={formErrors?.emailAddress}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="emailAddressPrivate"
                                        label={t("employees.emailAddressPrivate")}
                                        value={values.emailAddressPrivate}
                                        onChange={handleChange}
                                        error={formErrors?.emailAddressPrivate}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <Input
                                        name="googleMeetingLink"
                                        label={t("employees.googleMeetingLink")}
                                        value={values.googleMeetingLink}
                                        onChange={handleChange}
                                        error={formErrors?.googleMeetingLink}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="addressStreet"
                                        label={t("employees.addressStreet")}
                                        value={values.addressStreet}
                                        onChange={handleChange}
                                        error={formErrors?.addressStreet}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="addressNumber"
                                        label={t("employees.addressNumber")}
                                        value={values.addressNumber}
                                        onChange={handleChange}
                                        error={formErrors?.addressNumber}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="addressCity"
                                        label={t("employees.addressCity")}
                                        value={values.addressCity}
                                        onChange={handleChange}
                                        error={formErrors?.addressCity}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="addressZipCode"
                                        label={t("employees.addressZipCode")}
                                        value={values.addressZipCode}
                                        onChange={handleChange}
                                        error={formErrors?.addressZipCode}
                                    />
                                </Field>
                            </Row>
                            <Divider title="Social Media" />
                            <Row>
                                <Field>
                                    <Input
                                        name="socialMediaFacebook"
                                        label={t("employees.socialMediaFacebook")}
                                        value={values.socialMediaFacebook}
                                        onChange={handleChange}
                                        error={formErrors?.socialMediaFacebook}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="socialMediaInstagram"
                                        label={t("employees.socialMediaInstagram")}
                                        value={values.socialMediaInstagram}
                                        onChange={handleChange}
                                        error={formErrors?.socialMediaInstagram}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="socialMediaYoutube"
                                        label={t("employees.socialMediaYoutube")}
                                        value={values.socialMediaYoutube}
                                        onChange={handleChange}
                                        error={formErrors?.socialMediaYoutube}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="socialMediaLinkedIn"
                                        label={t("employees.socialMediaLinkedIn")}
                                        value={values.socialMediaLinkedIn}
                                        onChange={handleChange}
                                        error={formErrors?.socialMediaLinkedIn}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default AddEmployee;
